import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["integrations"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      onepasswordListItems: build.query<
        OnepasswordListItemsApiResponse,
        OnepasswordListItemsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/`,
          params: {
            firm_id: queryArg.firmId,
            user_id: queryArg.userId,
          },
        }),
        providesTags: ["integrations"],
      }),
      onepasswordCreateItem: build.mutation<
        OnepasswordCreateItemApiResponse,
        OnepasswordCreateItemApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/`,
          method: "POST",
          body: queryArg.createOnePasswordItemSchema,
        }),
        invalidatesTags: ["integrations"],
      }),
      onepasswordGetItem: build.query<
        OnepasswordGetItemApiResponse,
        OnepasswordGetItemApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/${queryArg.id}/`,
        }),
        providesTags: ["integrations"],
      }),
      onepasswordUpdateItem: build.mutation<
        OnepasswordUpdateItemApiResponse,
        OnepasswordUpdateItemApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateOnePasswordItemSchema,
        }),
        invalidatesTags: ["integrations"],
      }),
      onepasswordDeleteItem: build.mutation<
        OnepasswordDeleteItemApiResponse,
        OnepasswordDeleteItemApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["integrations"],
      }),
      onepasswordCreateFromUrl: build.mutation<
        OnepasswordCreateFromUrlApiResponse,
        OnepasswordCreateFromUrlApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/from-url/`,
          method: "POST",
          body: queryArg.createOnePasswordItemFromUrlSchema,
        }),
        invalidatesTags: ["integrations"],
      }),
      onepasswordGetMainCredentials: build.query<
        OnepasswordGetMainCredentialsApiResponse,
        OnepasswordGetMainCredentialsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/main-credentials/`,
          params: {
            firm_id: queryArg.firmId,
            type: queryArg["type"],
          },
        }),
        providesTags: ["integrations"],
      }),
      onepasswordGetUserCredentials: build.query<
        OnepasswordGetUserCredentialsApiResponse,
        OnepasswordGetUserCredentialsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/integrations/onepassword/items/user-credentials/`,
          params: {
            firm_id: queryArg.firmId,
            user_id: queryArg.userId,
          },
        }),
        providesTags: ["integrations"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type OnepasswordListItemsApiResponse =
  /** status 200  */ OnePasswordItemSchema[];
export type OnepasswordListItemsApiArg = {
  /** Filter by specific firm ID */
  firmId: string;
  /** Filter by specific user ID. If not provided, returns all items for the firm. */
  userId?: string;
};
export type OnepasswordCreateItemApiResponse =
  /** status 201  */ OnePasswordItemSchema;
export type OnepasswordCreateItemApiArg = {
  createOnePasswordItemSchema: CreateOnePasswordItemSchema;
};
export type OnepasswordGetItemApiResponse =
  /** status 200  */ OnePasswordItemSchema;
export type OnepasswordGetItemApiArg = {
  id: string;
  /** OnePassword item ID */
  pk: string;
};
export type OnepasswordUpdateItemApiResponse =
  /** status 200  */ OnePasswordItemSchema;
export type OnepasswordUpdateItemApiArg = {
  id: string;
  /** OnePassword item ID */
  pk: string;
  patchedUpdateOnePasswordItemSchema: UpdateOnePasswordItemSchema;
};
export type OnepasswordDeleteItemApiResponse = unknown;
export type OnepasswordDeleteItemApiArg = {
  id: string;
  /** OnePassword item ID */
  pk: string;
};
export type OnepasswordCreateFromUrlApiResponse =
  /** status 201  */ OnePasswordItemSchema;
export type OnepasswordCreateFromUrlApiArg = {
  createOnePasswordItemFromUrlSchema: CreateOnePasswordItemFromUrlSchema;
};
export type OnepasswordGetMainCredentialsApiResponse =
  /** status 200  */ OnePasswordItemSchema;
export type OnepasswordGetMainCredentialsApiArg = {
  /** Firm ID */
  firmId: string;
  /** Credential type (e.g., cms_credentials) */
  type: "cms_credentials";
};
export type OnepasswordGetUserCredentialsApiResponse =
  /** status 200  */ OnePasswordItemSchema;
export type OnepasswordGetUserCredentialsApiArg = {
  /** Firm ID */
  firmId: string;
  /** User ID */
  userId: string;
};
export type OnePasswordItemType = "cms_credentials";
export type OnePasswordUserSchema = {
  /** User ID */
  id: string | string;
  /** User's display name */
  display_name: string;
  /** User's email address */
  email: string;
};
export type OnePasswordVaultSchema = {
  /** Internal database ID */
  id: string | string;
  /** OnePassword vault ID */
  onepassword_id: string;
  /** Vault name/title */
  title: string;
};
export type OnePasswordItemSchema = {
  /** Internal database ID */
  id: string | string;
  /** Item type (e.g., cms_credentials) */
  type: OnePasswordItemType;
  /** Associated firm ID */
  firm_id?: (string | string) | null;
  /** Associated user ID. If None, this is the firm-level main credentials. */
  user_id?: (string | string) | null;
  /** User details if this is a user-specific credential */
  user?: OnePasswordUserSchema;
  /** Associated vault */
  vault: OnePasswordVaultSchema;
  /** OnePassword item ID */
  onepassword_id: string;
  /** Item name/title */
  title: string;
  /** OnePassword private URL for this item */
  private_url?: string | null;
  /** Whether this item is disabled and should not be used */
  is_disabled?: boolean;
};
export type CreateOnePasswordItemSchema = {
  /** OnePassword vault ID */
  vault_id: string;
  /** OnePassword item ID */
  item_id: string;
  /** Item type classification */
  type: OnePasswordItemType;
  /** Firm to associate with this item */
  firm_id?: (string | string) | null;
  /** User to associate with this item. If None, this becomes the firm-level main credentials. */
  user_id?: (string | string) | null;
};
export type UpdateOnePasswordItemSchema = {
  /** Item type classification */
  type?: OnePasswordItemType;
  /** Firm to associate with this item */
  firm_id?: (string | string) | null;
  /** User to associate with this item. If None, this becomes the firm-level main credentials. */
  user_id?: (string | string) | null;
  /** Whether this item is disabled and should not be used */
  is_disabled?: boolean | null;
};
export type CreateOnePasswordItemFromUrlSchema = {
  /** OnePassword private URL  */
  private_url: string;
  /** Item type classification */
  type: OnePasswordItemType;
  /** Firm to associate with this item */
  firm_id?: (string | string) | null;
  /** User to associate with this item. If None, this becomes the firm-level main credentials. */
  user_id?: (string | string) | null;
};
export const {
  useOnepasswordListItemsQuery,
  useOnepasswordCreateItemMutation,
  useOnepasswordGetItemQuery,
  useOnepasswordUpdateItemMutation,
  useOnepasswordDeleteItemMutation,
  useOnepasswordCreateFromUrlMutation,
  useOnepasswordGetMainCredentialsQuery,
  useOnepasswordGetUserCredentialsQuery,
} = injectedRtkApi;
